import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download, MousePointerClick, Sparkles, Store } from "lucide-react";
import { getPartnerClicksReport, listPartnerBrands } from "@/lib/partners";
import { publicAssetUrl, errorMessage } from "@/lib/api";

const isoOffset = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
};

export default function PartnerClicksReportPage() {
    const [brands, setBrands] = useState([]);
    const [filters, setFilters] = useState({ start: isoOffset(29), end: isoOffset(0), brand: "" });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await getPartnerClicksReport({
                start: filters.start,
                end: filters.end,
                partner_brand_slug: filters.brand || undefined,
            });
            setData(r);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { listPartnerBrands().then(setBrands).catch(() => {}); }, []);
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.start, filters.end, filters.brand]);

    const brandChartData = useMemo(
        () => (data?.by_brand || []).filter((b) => b.brand_slug !== "__unbranded__").map((b) => ({ name: b.brand_name, clicks: b.clicks_count })),
        [data],
    );

    const topBrand = data?.by_brand?.[0];

    const exportCsv = () => {
        if (!data) return;
        const rows = [["post_id", "title", "brand_slug", "brand_name", "clicks"]];
        (data.by_post || []).forEach((p) => rows.push([p.post_id, `"${(p.title || "").replace(/"/g, '""')}"`, p.brand_slug, p.brand_name, p.clicks_count]));
        const csv = rows.map((r) => r.join(",")).join("\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
        const a = document.createElement("a"); a.href = url; a.download = `partner-clicks-${filters.start}-to-${filters.end}.csv`; a.click(); URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="partner-clicks-report">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-sm font-medium text-orange-600">Finance · Reports</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900">Partner Click Report</h1>
                    <p className="mt-1 text-sm text-neutral-500">Track every outbound tap to a partner site — by brand, post, and day.</p>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                    <div>
                        <Label className="text-[10px] uppercase text-neutral-500">Start</Label>
                        <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="h-9 w-40" data-testid="pcr-start" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-neutral-500">End</Label>
                        <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="h-9 w-40" data-testid="pcr-end" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase text-neutral-500">Brand</Label>
                        <Select value={filters.brand || "__all__"} onValueChange={(v) => setFilters({ ...filters, brand: v === "__all__" ? "" : v })}>
                            <SelectTrigger className="h-9 w-40" data-testid="pcr-brand"><SelectValue placeholder="All brands" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__all__">All brands</SelectItem>
                                {brands.map((b) => (
                                    <SelectItem key={b.slug} value={b.slug}>{b.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <Button variant="outline" onClick={exportCsv} data-testid="pcr-export"><Download size={14} className="mr-1" /> CSV</Button>
                </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
                <Kpi label="Total Partner Clicks" value={data?.total_partner_clicks ?? 0} icon={MousePointerClick} tone="orange" testid="pcr-kpi-total" />
                <Kpi label="Unique Brands" value={data?.unique_brands_with_clicks ?? 0} icon={Store} tone="blue" testid="pcr-kpi-brands" />
                <Card className="rounded-2xl border-0 bg-gradient-to-br from-emerald-600 to-emerald-400 p-4 text-white" data-testid="pcr-kpi-top">
                    <div className="flex justify-between text-xs uppercase opacity-90"><span>Top Brand</span><Sparkles size={16} /></div>
                    <div className="mt-2 flex items-center gap-2 text-xl font-extrabold">
                        {topBrand?.logo_url && <img src={publicAssetUrl(topBrand.logo_url)} alt="" className="h-6 w-6 rounded bg-white/90 p-0.5" />}
                        {loading ? <Skeleton className="h-6 w-24 bg-white/40" /> : topBrand ? `${topBrand.brand_name} · ${topBrand.clicks_count}` : "—"}
                    </div>
                </Card>
            </div>

            <Card className="mt-4 rounded-2xl border border-neutral-200 p-4">
                <div className="mb-2 text-sm font-bold text-neutral-800">Clicks by brand</div>
                <div className="h-64">
                    {loading ? <Skeleton className="h-full w-full" /> : brandChartData.length === 0 ? (
                        <div className="flex h-full items-center justify-center text-xs text-neutral-500">No partner clicks in this window.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={brandChartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                <XAxis dataKey="name" fontSize={11} interval={0} angle={-10} textAnchor="end" height={50} />
                                <YAxis fontSize={11} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="clicks" fill="#f97316" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </Card>

            <Card className="mt-4 rounded-2xl border border-neutral-200">
                <div className="border-b p-3 text-sm font-bold text-neutral-800">Top posts by partner clicks</div>
                {loading ? <div className="space-y-2 p-4">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
                    : (data?.by_post || []).length === 0 ? <div className="p-8 text-center text-xs text-neutral-500">No matching posts.</div>
                    : <Table>
                        <TableHeader><TableRow>
                            <TableHead className="w-12">#</TableHead>
                            <TableHead>Post</TableHead>
                            <TableHead>Brand</TableHead>
                            <TableHead className="text-right">Clicks</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                            {(data.by_post || []).map((p, i) => (
                                <TableRow key={p.post_id} data-testid={`pcr-row-${p.post_id}`}>
                                    <TableCell className="text-xs text-neutral-500">{i + 1}</TableCell>
                                    <TableCell className="max-w-[280px] truncate font-medium">{p.title}</TableCell>
                                    <TableCell>
                                        <Badge variant="outline" className="text-[10px] uppercase">{p.brand_name}</Badge>
                                    </TableCell>
                                    <TableCell className="text-right font-mono font-bold">{p.clicks_count}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                }
            </Card>
        </div>
    );
}

function Kpi({ label, value, icon: Icon, tone, testid }) {
    const map = { orange: "from-orange-500 to-amber-400", blue: "from-blue-600 to-blue-400" };
    return (
        <Card className={`rounded-2xl border-0 bg-gradient-to-br p-4 text-white ${map[tone]}`} data-testid={testid}>
            <div className="flex justify-between text-xs uppercase opacity-90"><span>{label}</span><Icon size={16} /></div>
            <div className="mt-2 text-2xl font-extrabold">{value}</div>
        </Card>
    );
}
